import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/sharp-solid-svg-icons';
import type { ReactNode } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { SCView } from '../view';
import { Dnd, Sortable, useSortable } from './dnd';

export interface SCNavigationItemProps {
  value: any;
  label: string | ReactNode;
  icon?: IconProp;
  extra?: ReactNode;
  children?: SCNavigationItemProps[];
  disabled?: boolean;
}

interface SCNavigationProps {
  title?: string | ReactNode;
  items?: SCNavigationItemProps[];
  activeItem?: string;
  collapsible?: boolean;
  enableDnd?: boolean;
  initialExpand?: boolean;
  icon?: any;
  children: React.ReactNode;
  importStyle?: boolean;
  onActiveItem?: (item: SCNavigationItemProps) => void;
  onDnd?: (params: { activeValue: string; overValue: string }) => void;
}

function SCNavigationItem({
  item,
  showIcon,
  isSub,
  activeItem,
  onActiveItem,
  collapsed,
  initialExpand = true,
}: {
  item: SCNavigationItemProps;
  showIcon: boolean;
  isSub?: boolean;
  activeItem?: string;
  collapsed?: boolean;
  onActiveItem?: (item: SCNavigationItemProps) => void;
  initialExpand?: boolean;
}) {
  const [open, setOpen] = useState(initialExpand);
  const showChildrenIcon = item.children?.some((child) => child.icon);
  const propsSortable = useSortable({ value: item.value });

  return (
    <div
      {...propsSortable.attributes}
      {...propsSortable.listeners}
      ref={propsSortable.ref}
      style={propsSortable.style}
      className={
        activeItem === item.value
          ? 'eone-ui-navigation-menu-item-selected'
          : `sc-eone-ui-navigation-menu-item-container${isSub ? ' sukb' : ''}`
      }
    >
      <SCView
        flex
        gap={2}
        itemsCenter
        //className={className}
        //bg={bg}
        selectNone
        {...(item.disabled
          ? {
              cursorNotAllowed: true,
              style: { opacity: 0.5 },
            }
          : {
              cursorPointer: true,
              //hover: {
              //	bg: !propsSortable.isDragging ? '#68a1e8' : undefined,
              //},
            })}
        onClick={() => {
          if (item.disabled) return;
          //if (item.children && item.children.length > 0) {
          //	setOpen(!open);
          //}
          onActiveItem?.(item);
        }}
      >
        {showIcon && (
          <SCView
            className={
              activeItem === item.value
                ? 'sc-navigation-item-icon-selected'
                : 'sc-navigation-item-icon'
            }
          >
            {item.icon && <FontAwesomeIcon icon={item.icon} />}
          </SCView>
        )}
        {!collapsed && (
          <SCView
            flex1
            truncate
            title={typeof item.label === 'string' ? item.label : ''}
            className="eone-ui-navigation-menu-item-label-container"
          >
            {item.label}
          </SCView>
        )}
        {!collapsed && item.extra}
        {!collapsed && item.children && item.children.length > 0 && (
          <SCView
            onClick={(event) => {
              setOpen(!open);
              event.stopPropagation();
            }}
          >
            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          </SCView>
        )}
      </SCView>
      {item.children && !collapsed && (
        <Sortable values={item.children.map((child) => child.value)}>
          <SCView overflowHidden h={!open ? 0 : undefined}>
            {item.children.map((child) => (
              <SCNavigationItem
                key={child.value}
                item={child}
                activeItem={activeItem}
                showIcon={!!showChildrenIcon}
                isSub
                onActiveItem={onActiveItem}
              />
            ))}
          </SCView>
        </Sortable>
      )}
    </div>
  );
}

const SCNavigation = forwardRef<any, SCNavigationProps>((props, ref) => {
  const [collapsed, setCollapsed] = useState(false);

  const handleCollapsedChange = () => {
    setCollapsed(!collapsed);
  };

  const showIcon = props.items?.some((item) => item.icon);

  useImperativeHandle(ref, () => ({
    setCollapsed,
  }));

  const menuContainerClass = `eone-ui-navigation-menu-container ${collapsed ? 'collapsed' : 'expanded'}`;

  // NOTE - Specifically for the process tab in the 'Import' screen
  if (props.importStyle) {
    return (
      <Dnd items={props.items} onDnd={props.onDnd} enableDnd={props.enableDnd}>
        <div className="eone-ui-navigation-container">
          <div style={{ background: 'var(--surface-base-light)', minWidth: 216, paddingTop: 8 }}>
            {props.items?.map((item) => (
              <SCNavigationItem
                key={item.value}
                item={item}
                showIcon={false}
                activeItem={props.activeItem}
                collapsed={collapsed}
                onActiveItem={props.onActiveItem}
                initialExpand={props.initialExpand}
              />
            ))}
          </div>
          <div className="eone-ui-navigation-body">{props.children}</div>
        </div>
      </Dnd>
    );
  }

  return (
    <Dnd items={props.items} onDnd={props.onDnd} enableDnd={props.enableDnd}>
      <div className="eone-ui-navigation-container">
        <div className={menuContainerClass}>
          <div className="eone-ui-navigation-menu">
            {!collapsed && <div className="eone-ui-navigation-menu-header">{props.title}</div>}
            <div>
              {props.items?.map((item) => (
                <SCNavigationItem
                  key={item.value}
                  item={item}
                  showIcon={!!showIcon}
                  activeItem={props.activeItem}
                  collapsed={collapsed}
                  onActiveItem={props.onActiveItem}
                  initialExpand={props.initialExpand}
                />
              ))}
            </div>
          </div>
          {props.collapsible && (
            <SCView
              className="eone-ui-navigation-collapse-button-container"
              onClick={handleCollapsedChange}
            >
              <SCView
                className="eone-ui-navigation-collapse-button"
                rotate180={!collapsed}
                transitionTransform
              >
                <FontAwesomeIcon icon={faChevronRight} size="2x" />
              </SCView>
            </SCView>
          )}
        </div>

        <div className="eone-ui-navigation-body">{props.children}</div>
      </div>
    </Dnd>
  );
});

export { SCNavigation };
export type { SCNavigationProps };
